export type EngagementPlatform = 'instagram' | 'tiktok' | 'youtube';

export const engagementFormula: Record<EngagementPlatform, string> = {
  instagram: '(likes + comments) / followers × 100',
  tiktok: '(likes + comments + shares) / followers × 100',
  youtube: '(likes + comments) / views × 100',
};

export const creatorEarningsFormula =
  'Estimated earnings per post = average views / 1,000 x your rate per 1,000 views.';

export const shortsEarningsFormula =
  'Estimated monthly earnings = average public views x observed Shorts per month / 1,000 x RPM.';

export const twitchEarningsFormula =
  'Monthly total = subscription payouts + Prime payouts + bits payout + (average viewers x stream hours / 1,000 x ad CPM).';

export interface PostMetrics {
  likes?: number | null;
  comments?: number | null;
  shares?: number | null;
  views?: number | null;
}

export interface MoneyRange {
  low: number;
  high: number;
}

const money = (value: number) => Math.round(value * 100) / 100;
const percent = (value: number) => Math.round(value * 100) / 100;
const isCount = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

export const calculateEngagement = (
  platform: EngagementPlatform,
  followers: number | null,
  posts: PostMetrics[],
) => {
  if (platform !== 'youtube' && (!isCount(followers) || followers === 0)) {
    throw new Error('Followers must be greater than zero.');
  }
  const rates = posts.flatMap((post) => {
    if (!isCount(post.likes) || !isCount(post.comments)) return [];
    const shares = platform === 'tiktok' ? post.shares : 0;
    if (!isCount(shares)) return [];
    const denominator = platform === 'youtube' ? post.views : followers;
    if (!isCount(denominator) || denominator === 0) return [];
    const interactions = post.likes + post.comments + shares;
    return [(interactions / denominator) * 100];
  });
  if (rates.length === 0) throw new Error('Add at least one post with all required visible counts.');
  const sorted = [...rates].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
  return {
    sampleSize: rates.length,
    average: percent(rates.reduce((total, rate) => total + rate, 0) / rates.length),
    median: percent(median),
  };
};

export const estimateCreatorEarnings = ({
  averageViews,
  lowRatePerThousand,
  highRatePerThousand,
}: {
  averageViews: number;
  lowRatePerThousand: number;
  highRatePerThousand: number;
}): MoneyRange => {
  if (![averageViews, lowRatePerThousand, highRatePerThousand].every(isCount) || highRatePerThousand < lowRatePerThousand) {
    throw new Error('Enter a valid low-to-high sponsorship rate range.');
  }
  return {
    low: money((averageViews / 1_000) * lowRatePerThousand),
    high: money((averageViews / 1_000) * highRatePerThousand),
  };
};

export const calculateShortsEarnings = (
  averageViews: number,
  shortsPerMonth: number,
  lowRpm: number,
  highRpm: number,
): MoneyRange => {
  if (![averageViews, shortsPerMonth, lowRpm, highRpm].every(isCount) || highRpm < lowRpm) {
    throw new Error('Enter a valid low-to-high RPM range.');
  }
  const monthlyViews = averageViews * shortsPerMonth;
  return { low: money((monthlyViews / 1_000) * lowRpm), high: money((monthlyViews / 1_000) * highRpm) };
};

export interface TwitchInputs {
  tier1: number;
  prime: number;
  tier2: number;
  tier3: number;
  bits: number;
  averageViewers: number;
  streamHours: number;
  tier1Payout: number;
  primePayout: number;
  tier2Payout: number;
  tier3Payout: number;
  bitPayout: number;
  lowAdCpm: number;
  highAdCpm: number;
}

export const calculateTwitchEarnings = (input: TwitchInputs) => {
  if (!Object.values(input).every(isCount) || input.highAdCpm < input.lowAdCpm) {
    throw new Error('Enter non-negative values and a valid ad CPM range.');
  }
  const base = input.tier1 * input.tier1Payout + input.prime * input.primePayout
    + input.tier2 * input.tier2Payout + input.tier3 * input.tier3Payout + input.bits * input.bitPayout;
  const adImpressions = input.averageViewers * input.streamHours;
  return {
    base: money(base),
    low: money(base + (adImpressions / 1_000) * input.lowAdCpm),
    high: money(base + (adImpressions / 1_000) * input.highAdCpm),
  };
};
